import { Funnel } from '../types/funnel.types';
import { FunnelElement } from '../types/element.types';
import { FunnelSection } from '../types/section.types';
import { stylesToCss } from './helpers';
import { downloadFile } from './helpers';
import { validateFunnelForSave } from './validation';

/**
 * Export funnel as JSON
 */
export const exportToJSON = (funnel: Funnel): void => {
  const validation = validateFunnelForSave(funnel);
  
  if (!validation.valid) {
    alert(validation.errors.join('\n'));
    return;
  }

  const json = JSON.stringify(funnel, null, 2);
  const filename = `${funnel.name.replace(/\s+/g, '-').toLowerCase()}-${Date.now()}.json`;
  downloadFile(json, filename, 'application/json');
};

/**
 * Generate HTML for an element
 */
const generateElementHTML = (element: FunnelElement): string => {
  const styles = stylesToCss(element.styles);
  const styleAttr = styles ? ` style="${styles}"` : '';

  switch (element.type) {
    case 'heading': {
      const level = element.settings?.level || 'h2';
      return `<${level}${styleAttr}>${element.content}</${level}>`;
    }

    case 'text':
      return `<p${styleAttr}>${element.content}</p>`;

    case 'bulletedList': {
      const items = Array.isArray(element.content)
        ? element.content.map((item) => `<li>${item}</li>`).join('')
        : '';
      return `<ul${styleAttr}>${items}</ul>`;
    }

    case 'image': {
      const { src, alt } = element.content || { src: '', alt: '' };
      return `<img src="${src}" alt="${alt}"${styleAttr} />`;
    }

    case 'video': {
      const { url, provider } = element.content || { url: '', provider: 'youtube' };
      if (provider === 'youtube') {
        const videoId = url.split('v=')[1] || url.split('/').pop();
        return `<iframe${styleAttr} src="https://www.youtube.com/embed/${videoId}" frameborder="0" allowfullscreen></iframe>`;
      }
      return `<video${styleAttr} src="${url}" controls></video>`;
    }

    case 'button': {
      const link = element.settings?.link || '#';
      const target = element.settings?.openInNewTab ? ' target="_blank"' : '';
      return `<a href="${link}"${target}${styleAttr}>${element.content}</a>`;
    }

    case 'input': {
      const { placeholder, inputType, name } = element.settings || {};
      return `<input type="${inputType || 'text'}" placeholder="${placeholder || ''}" name="${name || ''}"${styleAttr} />`;
    }

    case 'textarea': {
      const { placeholder, rows, name } = element.settings || {};
      return `<textarea placeholder="${placeholder || ''}" rows="${rows || 4}" name="${name || ''}"${styleAttr}></textarea>`;
    }

    case 'checkbox': {
      const { label, name } = element.settings || {};
      return `<label${styleAttr}><input type="checkbox" name="${name || ''}" /> ${label || ''}</label>`;
    }

    case 'select': {
      const { options, name } = element.settings || { options: [] };
      const optionsHTML = options
        .map((opt: any) => `<option value="${opt.value}">${opt.label}</option>`)
        .join('');
      return `<select name="${name || ''}"${styleAttr}>${optionsHTML}</select>`;
    }

    case 'spacer': {
      const height = element.settings?.height || '40px';
      return `<div style="height: ${height};"></div>`;
    }

    case 'divider': {
      const thickness = element.settings?.thickness || '1px';
      const color = element.settings?.color || '#e5e7eb';
      const dividerStyle = element.settings?.style || 'solid';
      return `<hr style="border: none; border-top: ${thickness} ${dividerStyle} ${color}; ${styles}" />`;
    }

    case 'icon': {
      const { iconName } = element.content || { iconName: 'Star' };
      return `<span${styleAttr}>${iconName}</span>`;
    }

    case 'customHtml':
      return element.content || '';

    default:
      return `<div${styleAttr}>${JSON.stringify(element.content)}</div>`;
  }
};

/**
 * Generate HTML for a section
 */
const generateSectionHTML = (section: FunnelSection): string => {
  const { backgroundColor, padding, margin } = section.settings;
  
  const sectionStyles = [
    backgroundColor ? `background-color: ${backgroundColor}` : '',
    padding ? `padding: ${padding.top}px ${padding.right}px ${padding.bottom}px ${padding.left}px` : '',
    margin ? `margin: ${margin.top}px ${margin.right}px ${margin.bottom}px ${margin.left}px` : '',
  ]
    .filter(Boolean)
    .join('; ');

  const columnsHTML = section.columns
    .map((column) => {
      const columnWidth = (column.span / 12) * 100;
      const elementsHTML = column.elements.map(generateElementHTML).join('\n');
      return `
        <div class="column" style="flex: 0 0 ${columnWidth}%; max-width: ${columnWidth}%;">
          ${elementsHTML}
        </div>
      `;
    })
    .join('');

  return `
    <section class="funnel-section" style="${sectionStyles}">
      <div class="container" style="max-width: ${section.settings.maxWidth || '1200px'}; margin: 0 auto;">
        <div class="row" style="display: flex; flex-wrap: wrap; gap: 20px;">
          ${columnsHTML}
        </div>
      </div>
    </section>
  `;
};

/**
 * Export funnel as standalone HTML
 */
export const exportToHTML = (funnel: Funnel): void => {
  const validation = validateFunnelForSave(funnel);
  
  if (!validation.valid) {
    alert(validation.errors.join('\n'));
    return;
  }

  const sectionsHTML = funnel.sections.map(generateSectionHTML).join('\n');

  const html = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${funnel.settings?.title || funnel.name}</title>
  ${funnel.settings?.seo?.metaDescription ? `<meta name="description" content="${funnel.settings.seo.metaDescription}">` : ''}
  ${funnel.settings?.seo?.metaKeywords ? `<meta name="keywords" content="${funnel.settings.seo.metaKeywords.join(', ')}">` : ''}
  ${funnel.settings?.seo?.ogImage ? `<meta property="og:image" content="${funnel.settings.seo.ogImage}">` : ''}
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
      line-height: 1.6;
    }
    
    img {
      max-width: 100%;
      height: auto;
    }
    
    .funnel-section {
      width: 100%;
    }
    
    .container {
      width: 100%;
      padding: 0 20px;
    }
    
    .row {
      display: flex;
      flex-wrap: wrap;
      margin: 0 -10px;
    }
    
    .column {
      padding: 0 10px;
    }
    
    /* Responsive styles */
    @media (max-width: 768px) {
      .column {
        flex: 0 0 100% !important;
        max-width: 100% !important;
      }
    }
    
    ${funnel.settings?.customCss || ''}
  </style>
</head>
<body>
  ${sectionsHTML}
  
  ${funnel.settings?.customJs ? `<script>${funnel.settings.customJs}</script>` : ''}
</body>
</html>
  `.trim();

  const filename = `${funnel.name.replace(/\s+/g, '-').toLowerCase()}-${Date.now()}.html`;
  downloadFile(html, filename, 'text/html');
};

